<template>
  <div>
    <div class="tile">
      <div class="form-inline">
        <b-form-input type="month" v-model="period" class="mr-1" @change="load"></b-form-input>
        <button @click="load" class="btn btn-primary mr-1">{{ $t('search') }}</button>
        <button @click="exportExcel" class="btn btn-primary mr-1" :disabled="!rows.length">
          <i class="fa fa-download"></i> {{ $t('exportExcel') }}
        </button>
      </div>
    </div>

    <div class="tile">
      <div class="table-responsive c-template-table">
        <table class="table table-hover table-bordered text-center">
          <caption class="text-center text-uppercase font-weight-bold" style="caption-side: top; color: #000;">
            {{ $t('reportTitle') }} {{ monthLabel }}
          </caption>
          <thead>
            <tr>
              <th>{{ $t('stt') }}</th>
              <th>{{ $t('fullName') }}</th>
              <th>{{ $t('position') }}</th>
              <th>{{ $t('kpiForCommission') }}</th>
              <th>{{ $t('callFeeUnder12', [monthLabel]) }}</th>
              <th>{{ $t('commissionPercent') }}</th>
              <th>{{ $t('callFeeOver12Big') }}</th>
              <th>{{ $t('commissionPercent') }}</th>
              <th class="text-danger">{{ $t('commissionReceived') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in rows" :key="item.userId">
              <td>{{ index + 1 }}</td>
              <td>{{ item.name }}</td>
              <td>{{ position(item.role) }}</td>
              <td>{{ item.kpiBracket }}</td>
              <td class="text-right">{{ item.feeUnder12 | numFormat }}</td>
              <td>{{ percent(item.pctUnder12) }}</td>
              <td class="text-right">{{ item.feeOver12Big | numFormat }}</td>
              <td>{{ percent(item.pctOver12Big) }}</td>
              <td class="text-right font-weight-bold text-danger">
                <span v-if="item.commission > 0">{{ item.commission | numFormat }}</span>
                <span v-else>-</span>
              </td>
            </tr>
            <tr v-if="!rows.length">
              <td colspan="9" class="text-danger">{{ $t('noData') }}</td>
            </tr>
            <tr v-if="rows.length" class="font-weight-bold" style="background-color: #fcf8e3;">
              <td colspan="4" class="text-left">{{ $t('grandTotal') }}</td>
              <td class="text-right">{{ total.feeUnder12 | numFormat }}</td>
              <td></td>
              <td class="text-right">{{ total.feeOver12Big | numFormat }}</td>
              <td></td>
              <td class="text-right text-danger">{{ total.commission | numFormat }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import Excel from 'exceljs';
import { saveAs } from 'file-saver';

export default {
  data() {
    return {
      period: moment().format('YYYY-MM'),
      items: [],
    }
  },
  computed: {
    monthLabel() {
      return moment(this.period, 'YYYY-MM').format('MM/YYYY');
    },
    rows() {
      // Sắp xếp: khu vực (VP Miền Bắc trước), trong khu vực theo hoa hồng giảm dần
      return [...this.items].sort((a, b) => {
        let office = (a.office || '').localeCompare(b.office || '');
        if (office !== 0) {
          return office;
        }
        return b.commission - a.commission;
      });
    },
    total() {
      return this.items.reduce((acc, item) => {
        acc.feeUnder12 += item.feeUnder12 || 0;
        acc.feeOver12Big += item.feeOver12Big || 0;
        acc.commission += item.commission || 0;
        return acc;
      }, { feeUnder12: 0, feeOver12Big: 0, commission: 0 });
    }
  },
  methods: {
    load() {
      let [year, month] = this.period.split('-');
      let loader = this.$loading.show();
      this.$service.get('/report/call-fee-commission', {
        params: { month: parseInt(month), year: parseInt(year) }
      }).then(response => {
        this.items = response.data;
      }).finally(() => {
        loader.hide();
      });
    },
    percent(value) {
      return (value || 0).toFixed(2) + '%';
    },
    position(role) {
      const map = {
        supervisor: this.$t('roleSupervisor'),
        manager: this.$t('roleManager'),
      };
      return map[role] || this.$t('roleConsultant');
    },
    xlsxFillRow(row, argb, count) {
      for (let c = 1; c <= count; c++) {
        row.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
      }
    },
    xlsxBorderRange(sheet, r1, r2, c1, c2) {
      let thin = { style: 'thin', color: { argb: 'FFBFBFBF' } };
      for (let r = r1; r <= r2; r++) {
        let row = sheet.getRow(r);
        for (let c = c1; c <= c2; c++) {
          let cell = row.getCell(c);
          cell.border = { top: thin, left: thin, bottom: thin, right: thin };
          cell.alignment = Object.assign({ vertical: 'middle' }, cell.alignment || {});
        }
      }
    },
    async exportExcel() {
      let money = '#,##0';
      let workbook = new Excel.Workbook();
      let sheet = workbook.addWorksheet(this.$t('sheetCallFee'));
      sheet.columns = [
        { header: this.$t('stt'), key: 'stt', width: 6 },
        { header: this.$t('fullName'), key: 'name', width: 22 },
        { header: this.$t('position'), key: 'position', width: 16 },
        { header: this.$t('kpiForCommission'), key: 'kpiBracket', width: 16 },
        { header: this.$t('callFeeUnder12', [this.monthLabel]), key: 'feeUnder12', width: 22, style: { numFmt: money } },
        { header: this.$t('commissionPercent'), key: 'pctUnder12', width: 12 },
        { header: this.$t('callFeeOver12Big'), key: 'feeOver12Big', width: 24, style: { numFmt: money } },
        { header: this.$t('commissionPercent') + ' ', key: 'pctOver12Big', width: 12 },
        { header: this.$t('commissionReceived'), key: 'commission', width: 18, style: { numFmt: money } },
      ];
      this.rows.forEach((item, index) => {
        sheet.addRow({
          stt: index + 1,
          name: item.name,
          position: this.position(item.role),
          kpiBracket: item.kpiBracket,
          feeUnder12: item.feeUnder12 || 0,
          pctUnder12: this.percent(item.pctUnder12),
          feeOver12Big: item.feeOver12Big || 0,
          pctOver12Big: this.percent(item.pctOver12Big),
          commission: item.commission || 0,
        });
      });
      if (this.rows.length) {
        let totalRow = sheet.addRow({
          name: this.$t('grandTotal'),
          feeUnder12: this.total.feeUnder12,
          feeOver12Big: this.total.feeOver12Big,
          commission: this.total.commission,
        });
        totalRow.font = { bold: true, color: { argb: 'FFC00000' } };
        this.xlsxFillRow(totalRow, 'FFFCF8E3', 9);
      }
      sheet.getRow(1).font = { bold: true };
      sheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      this.xlsxFillRow(sheet.getRow(1), 'FFF2F2F2', 9);
      sheet.views = [{ state: 'frozen', ySplit: 1 }];
      this.xlsxBorderRange(sheet, 1, sheet.rowCount, 1, 9);

      let buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `hoa-hong-cuoc-goi-${this.period}.xlsx`);
    }
  },
  mounted() {
    this.load();
  },
  i18n: {
    messages: {
      en: {
        reportTitle: 'Call fee month',
        stt: 'No.',
        fullName: 'Full name',
        position: 'Position',
        kpiForCommission: 'KPI for commission',
        callFeeUnder12: 'Call fee {0} (customers under 12 months)',
        callFeeOver12Big: 'Call fee customers >15 users, >12 months',
        commissionPercent: '% Commission',
        commissionReceived: 'Commission received',
        roleConsultant: 'Consultant',
        roleSupervisor: 'Supervisor',
        roleManager: 'Manager',
        grandTotal: 'Grand total',
        exportExcel: 'Export Excel',
        sheetCallFee: 'Call fee commission',
        noData: 'No KPI data for this month',
      },
      vi: {
        reportTitle: 'Cước gọi tháng',
        stt: 'STT',
        fullName: 'Họ tên',
        position: 'Chức vụ',
        kpiForCommission: 'KPI tính hoa hồng',
        callFeeUnder12: 'Cước gọi tháng {0} (khách hàng dưới 12 tháng)',
        callFeeOver12Big: 'Cước gọi khách hàng >15 user, >12 tháng',
        commissionPercent: '% Hoa hồng nhận',
        commissionReceived: 'Hoa hồng nhận được',
        roleConsultant: 'Nhân viên tư vấn',
        roleSupervisor: 'Trưởng nhóm',
        roleManager: 'Quản lý',
        grandTotal: 'Tổng cộng',
        exportExcel: 'Xuất dữ liệu',
        sheetCallFee: 'Hoa hồng cước gọi',
        noData: 'Chưa có dữ liệu KPI cho tháng này',
      }
    }
  }
}
</script>

<style scoped>
.c-template-table table.table thead th {
  background-color: #f2f2f2 !important;
}
</style>
